import { type FC, type FormEvent, type KeyboardEvent, useState } from "react";
import cx from "classnames";

import styles from "./ChatInput.module.css";
import { Button } from "../Button";
import { UnstyledButton } from "../UnstyledButton";
import CrossIcon from "./assets/cross.svg?react";
import ArrowUpIcon from "./assets/arrow-up.svg?react";
import StopIcon from "./assets/stop.svg?react";
import { ButtonSpinner } from "../ButtonSpinner";
import { useI18n } from "../../i18n";

type Props = {
  status: "idle" | "sending" | "awaiting" | "rendering" | "error";
  placeholder?: string;
  disabled?: boolean;
  isGenerating?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onSubmit: (message: string) => void;
  onStop?: () => void;
};

export const ChatInput: FC<Props> = ({
  status,
  placeholder,
  disabled,
  isGenerating,
  onFocus,
  onBlur,
  onSubmit,
  onStop,
}) => {
  const i18n = useI18n();
  const [value, setValue] = useState("");

  const trimmedValue = value.trim();
  const canSubmitText = trimmedValue.length > 0;
  const canStop = Boolean(isGenerating && onStop) && !canSubmitText;
  const isBusy = status === "sending" || status === "awaiting";
  const showButton = canSubmitText || canStop || isBusy;
  const isInputDisabled = disabled ?? false;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (canSubmitText) {
      onSubmit(trimmedValue);
      setValue("");
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSubmitText) {
        onSubmit(trimmedValue);
        setValue("");
      }
    }
  };

  return (
    <form className={styles.chatInput} onSubmit={handleSubmit}>
      <div className={styles.textareaWrapper}>
        <input
          type="text"
          name="message"
          className={cx(styles.textarea)}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={isInputDisabled}
        />
        {value.length > 0 ? (
          <UnstyledButton
            className={styles.clear}
            onClick={() => setValue("")}
            aria-label={i18n.chatInput.clearMessage}
          >
            <CrossIcon />
          </UnstyledButton>
        ) : null}
      </div>

      {showButton && (canStop ? (
        <Button
          type="button"
          appearance="secondary"
          className={styles.submit}
          onPointerDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            onStop?.();
          }}
        >
          <StopIcon />
        </Button>
      ) : (
        <Button
          type="submit"
          appearance="secondary"
          className={styles.submit}
          onPointerDown={(e) => e.preventDefault()}
        >
          {status === "sending" ? (
            <ButtonSpinner />
          ) : (
            <ArrowUpIcon />
          )}
        </Button>
      ))}
    </form>
  );
};
